import {
  DocumentTypesInfiniteSelectField,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import { PaymentCondition } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import { MathRound } from '@/utils/functions/MathRound';
import { getTableScrollX } from '@/utils/table';
import { Typography, Form, Table, Input } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import {
  WorkOrderFinancialFormValues,
  WorkorderFinancialInstallment,
} from '../WorkOrderFinancialConfig';
import DocumentTypeColumnFilter from '@/components/_common/DocumentType/DocumentTypeColumnFilter';
import formatterPrice from '@/utils/number/formatterPrice';
import { currencyFormatter } from '@/utils/number';
import './styles.less';
import {
  DocumentType,
  DocumentTypePaymentType,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';

type Props = {
  paymentCondition?: PaymentCondition;
  documentType?: DocumentType;
  installments: WorkorderFinancialInstallment[];
  setInstallments: React.Dispatch<
    React.SetStateAction<WorkorderFinancialInstallment[]>
  >;
  isStone: boolean;
};

const WorkOrderFinancialInstallments: React.FC<Props> = ({
  paymentCondition,
  documentType,
  installments,
  setInstallments,
  isStone,
}) => {
  const form = Form.useFormInstance<WorkOrderFinancialFormValues>();
  const formValues = Form.useWatch([], form);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const discountIncrise =
    -(formValues?.discountPrice || 0) + (formValues?.incrisePrice || 0);

  const installmentRestAmount =
    (formValues?.negotiated || 0) -
    installments.reduce((acc, cur) => acc + (cur.installmentBaseValue || 0), 0);

  useEffect(() => {
    if (formValues?.documentTypeId) {
      setDocumentTypeId(formValues.documentTypeId);
    } else {
      setDocumentTypeId(undefined);
    }
  }, [formValues?.documentTypeId]);

  useEffect(() => {
    createInstallments();
  }, [
    formValues?.installmentQuantity,
    formValues?.installmentsInterval,
    formValues?.dueDate,
    formValues?.negotiated,
    formValues?.discountPrice,
    formValues?.incrisePrice,
    paymentCondition,
  ]);

  useEffect(() => {
    form.setFieldValue('installments', installments);
  }, [installments]);

  function createInstallments() {
    const totalValue = formValues?.negotiated || 0;
    const installmentQuantity = formValues?.installmentQuantity;

    const discountIncriseValue = MathRound(
      discountIncrise / installmentQuantity,
      2,
    );

    if (typeof installmentQuantity === 'number') {
      const newInstallments = new Array(installmentQuantity || 0)
        .fill(0)
        .map<WorkorderFinancialInstallment>((_, index) => {
          const prevItems = installments[index] || {};
          let installmentValueItem: number;
          let discountIncriseValueItem: number;
          let installmentValueBase: number;
          let dueDate: string;

          if (
            (isStone && index === 0) ||
            (!isStone && index === installmentQuantity - 1)
          ) {
            const installmentBase = MathRound(
              totalValue / installmentQuantity,
              2,
            );

            installmentValueBase = MathRound(
              totalValue -
                installmentBase * installmentQuantity +
                installmentBase,
              2,
            );
          } else {
            installmentValueBase = MathRound(
              totalValue / installmentQuantity,
              2,
            );
          }

          if (!paymentCondition) {
            if (index === installmentQuantity - 1) {
              discountIncriseValueItem = MathRound(
                discountIncrise -
                  discountIncriseValue * installmentQuantity +
                  discountIncriseValue,
                2,
              );
              installmentValueItem = MathRound(
                installmentValueBase + discountIncriseValueItem,
                2,
              );
            } else {
              discountIncriseValueItem = discountIncriseValue;
              installmentValueItem = MathRound(
                installmentValueBase + discountIncriseValueItem,
                2,
              );
            }

            const paymentType = documentType?.paymentType;

            if (typeof paymentType !== 'undefined' && isStone) {
              const interval =
                paymentType === DocumentTypePaymentType.Credit ? 30 : 1;

              dueDate = moment(formValues?.dueDate)
                .clone()
                ?.add(interval * (index + 1), 'days')
                ?.format();
            } else {
              dueDate =
                index === 0
                  ? formValues?.dueDate?.format()
                  : moment(formValues?.dueDate)
                      .clone()
                      ?.add(
                        (formValues?.installmentsInterval || 0) * index,
                        'days',
                      )
                      ?.format();
            }
          } else {
            installmentValueItem = MathRound(
              (paymentCondition.percentage / 100) * installmentValueBase +
                installmentValueBase,
              2,
            );
            discountIncriseValueItem = MathRound(
              (paymentCondition.percentage / 100) * installmentValueBase,
              2,
            );

            dueDate = moment()
              ?.clone()
              .add(paymentCondition?.installments[index]?.numberOfDays, 'days')
              .format();
          }

          return {
            ...prevItems,
            discountIncrease: discountIncriseValueItem,
            dueDate,
            installmentNumber: index + 1,
            installmentValue: installmentValueItem,
            baseValue: installmentValueBase,
            installmentBaseValue: installmentValueBase,
            documentTypeId:
              prevItems?.documentTypeId || formValues?.documentTypeId,
          };
        });

      setInstallments(newInstallments);
    } else {
      setInstallments([]);
    }
  }

  function setDocumentTypeId(documentTypeId?: number) {
    if (documentTypeId) {
      setInstallments((state) =>
        state.map((item) => ({
          ...item,
          documentTypeId,
        })),
      );
    } else {
      setInstallments([]);
    }
    setFilterDropdownOpen(false);
  }

  function handleField(
    pathKey: [number, keyof WorkorderFinancialInstallment],
    value: unknown,
  ) {
    setInstallments((state) =>
      state.map((item, index) =>
        index === pathKey[0]
          ? {
              ...item,
              [pathKey[1]]: value,
            }
          : item,
      ),
    );
  }

  function handleFields(
    values: Record<
      number,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Partial<Record<keyof WorkorderFinancialInstallment, any>>
    >,
  ) {
    setInstallments((state) =>
      state.map((item, index) => ({
        ...item,
        ...values[index],
      })),
    );
  }

  const columns: ColumnsType<WorkorderFinancialInstallment> = [
    {
      dataIndex: 'installmentNumber',
      title: 'Parcela',
      width: 80,
      render: (value, _, index) => {
        <Form.Item name={[index, 'installmentNumber']} hidden>
          <Input />
        </Form.Item>;
        return value;
      },
    },
    {
      dataIndex: 'documentTypeId',
      title: 'Tipo de documento',
      width: 200,
      filterDropdownOpen,
      onFilterDropdownOpenChange: setFilterDropdownOpen,
      filterDropdown: () => (
        <DocumentTypeColumnFilter
          onChange={setDocumentTypeId}
          queryFilter={{ isStoneDocument: isStone }}
        />
      ),
      filterIcon: <FaExchangeAlt size={14} />,
      render: (value, __, index) => {
        if (isStone) {
          return documentType?.description || '-';
        }

        return (
          <DocumentTypesInfiniteSelectField
            queryFilter={{ isStoneDocument: isStone }}
            value={value}
            onChange={(nextValue) => {
              handleField([index, 'documentTypeId'], nextValue);
            }}
          />
        );
      },
    },
    {
      dataIndex: 'dueDate',
      title: 'Data de vencimento',
      width: 100,
      render: (value, _, index) => {
        if (isStone) {
          return moment(value).format('DD/MM/YYYY');
        }

        return (
          <InputDate
            value={moment(value)}
            onChange={(value) => {
              handleFields({
                [index]: {
                  dueDate: value?.format(),
                },
              });
            }}
          />
        );
      },
    },
    {
      dataIndex: 'discountIncrease',
      title: 'Desc./Acres.($)',
      width: 100,
      render: (value) => formatterPrice(value || 0),
    },
    {
      dataIndex: 'baseValue',
      title: 'Valor base',
      width: 100,
      render: (value) => formatterPrice(value),
    },
    {
      dataIndex: 'installmentBaseValue',
      title: 'Valor da parcela',
      width: 100,
      render: (value, row, index) => {
        if (isStone) {
          if (value < 0.1 && installments.length <= 12) {
            return (
              <span style={{ color: 'red' }}>{currencyFormatter(value)}</span>
            );
          } else if (value < 10 && installments.length > 12) {
            return (
              <span style={{ color: 'red' }}>{currencyFormatter(value)}</span>
            );
          }
          return currencyFormatter(value);
        }

        return (
          <InputDecimal
            value={value}
            onChange={(value?: number) => {
              const nextValue = value || 0;

              handleFields({
                [index]: {
                  installmentValue: nextValue + (row.discountIncrease || 0),
                  installmentBaseValue: nextValue,
                },
              });
            }}
            min={0}
            max={installmentRestAmount + (row.installmentBaseValue || 0)}
          />
        );
      },
    },

    {
      dataIndex: 'installmentValue',
      title: 'Total da parcela($)',
      align: 'right',
      width: 100,
      render: (value) => <strong>{formatterPrice(value)}</strong>,
    },
  ];

  return (
    <>
      <Typography.Title level={4}>Parcelas</Typography.Title>

      <Table
        dataSource={installments}
        columns={columns}
        pagination={false}
        scroll={{ x: getTableScrollX(columns) }}
      />
    </>
  );
};

export default WorkOrderFinancialInstallments;
